import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    useToast,
    Text,
    Box,
} from "@chakra-ui/react";

import React, { useState } from "react";
import ReportsApi from "../../../../api/reports";
import MonthDropdownSelection from "../../../../functions/MonthDropdownSelection";
import { downloadEndOfMonthSummary } from "../../../../functions/generatePayrollFile";

export default function EndOfMonthSummaryModal(props) {
    const { isOpen, onClose } = props;
    const toast = useToast();
    
    const [selectedMonth, setSelectedMonth] = useState("");
    const [additionalEmail, setAdditionalEmail] = useState("");
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const handleDownload = () => {
        if (!selectedMonth) {
            toast({
                title: 'נא לבחור חודש',
                position: 'top',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsDownloading(true);
        
        ReportsApi.getEndOfMonthReportsSummary(selectedMonth)
            .then(res => {
                const summaryData = res.data;
                
                // Generate and download Excel file
                downloadEndOfMonthSummary(summaryData, selectedMonth);
                
                toast({
                    title: 'הדוח הורד בהצלחה',
                    description: `נמצאו ${summaryData.length} חברות`,
                    position: 'top',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            })
            .catch(e => {
                console.error('Error downloading summary:', e);
                toast({
                    title: 'שגיאה בהורדת הדוח',
                    description: e.message,
                    position: 'top',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            })
            .finally(() => {
                setIsDownloading(false);
            });
    };

    const handleSendEmail = () => {
        if (!selectedMonth) {
            toast({
                title: 'נא לבחור חודש',
                position: 'top',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Validate additional email if provided
        if (additionalEmail && !isValidEmail(additionalEmail)) {
            toast({
                title: 'כתובת אימייל לא תקינה',
                description: 'נא להזין כתובת אימייל תקינה',
                position: 'top',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsSendingEmail(true);

        ReportsApi.sendEndOfMonthSummaryEmail(selectedMonth, additionalEmail)
            .then(res => {
                toast({
                    title: 'הדוח נשלח בהצלחה',
                    description: 'הדוח נשלח לrepots@payro.io במייל',
                    position: 'top',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                onClose();
            })
            .catch(e => {
                console.error('Error sending email:', e);
                toast({
                    title: 'שגיאה בשליחת המייל',
                    description: e.message,
                    position: 'top',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            })
            .finally(() => {
                setIsSendingEmail(false);
            });
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleClose = () => {
        if (!isDownloading && !isSendingEmail) {
            setSelectedMonth("");
            setAdditionalEmail("");
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} isCentered closeOnOverlayClick={false} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>דוח סיכום חודשי</ModalHeader>
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        {/* Month Selection */}
                        <FormControl>
                            <FormLabel>בחר חודש</FormLabel>
                            <MonthDropdownSelection handleSelectedMonth={setSelectedMonth} />
                        </FormControl>

                        {/* Additional Email Input */}
                        <FormControl>
                            <FormLabel>אימייל נוסף (אופציונלי)</FormLabel>
                            <Input
                                type="email"
                                placeholder="example@company.com"
                                value={additionalEmail}
                                onChange={(e) => setAdditionalEmail(e.target.value)}
                                dir="ltr"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                הדוח ישלח למשרד (reports@payro.io) ולאימייל הנוסף אם צוין
                            </Text>
                        </FormControl>

                        {/* Info Box */}
                        <Box p={3} bg="blue.50" borderRadius="md">
                            <Text fontSize="sm" color="blue.800">
                                הדוח כולל: שם לקוח, שם משפטי, דרישת תשלום מקדמות, חשבונית דמי מנוי (לא כולל מע״מ), חשבונית דמי מנוי (כולל מע״מ), ועמלות העברה (כולל מע״מ)
                            </Text>
                        </Box>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={3}>
                        <Button
                            colorScheme="green"
                            onClick={handleSendEmail}
                            isLoading={isSendingEmail}
                            isDisabled={isDownloading || !selectedMonth}
                        >
                            שלח למשרד במייל
                        </Button>
                        <Button
                            colorScheme="blue"
                            variant="outline"
                            onClick={handleDownload}
                            isLoading={isDownloading}
                            isDisabled={isSendingEmail || !selectedMonth}
                        >
                            הורד קובץ
                        </Button>
                        <Button
                            onClick={handleClose}
                            isDisabled={isDownloading || isSendingEmail}
                        >
                            סגור
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

